import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IconButton, InputAdornment, Slider, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import { useGetAiProvidersQuery } from 'services/aiProviderApi';
import ExpandableTextArea from 'components/ExpandableTextArea';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDBox from 'components/MDBox';

/**
 * Component for displaying and managing AI model details
 * Allows viewing, creating, and updating models
 */
function AiModelDetails({ model, onSubmit = () => {}, onFieldChange = () => {} }) {
  const { formatMessage: msg } = useIntl();
  const { data: providers = [] } = useGetAiProvidersQuery();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      code: '',
      label: '',
      description: '',
      temperature: 0.7,
      maxTokens: '',
      provider: null
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetModel = useCallback(() => {
    if (model?.code) {
      reset(model);
    } else {
      reset({
        id: '',
        code: '',
        label: '',
        description: '',
        temperature: 0.7,
        maxTokens: '',
        provider: null
      });
    }
  }, [model, reset]);

  useEffect(() => {
    resetModel();
  }, [model, resetModel]);

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const handleReset = () => {
    resetModel();
  };

  const isCreating = !model?.id;

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <SmartToyIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({
            id: isCreating ? 'page.aiModels.create.title' : 'page.aiModels.edit.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <Controller
            name="id"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.id.label' })}
                variant="filled"
                fullWidth
                required
                size="small"
                InputProps={{
                  readOnly: true
                }}
              />
            )}
          />
        )}
        <Controller
          name="code"
          control={control}
          rules={{ required: msg({ id: 'field.code.required' }) }}
          render={({ field }) => (
            <MDInput
              required
              {...field}
              label={msg({ id: 'field.code.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.code}
              helperText={errors.code ? errors.code.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={clearField}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="label"
          control={control}
          rules={{ required: msg({ id: 'field.label.required' }) }}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.label.label' })}
              variant="filled"
              fullWidth
              required
              size="small"
              error={!!errors.label}
              helperText={errors.label ? errors.label.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('label')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <MDBox sx={{ height: '200px' }}>
              <ExpandableTextArea
                field={field}
                error={!!errors.description}
                helperText={errors.description ? errors.description.message : ''}
                clearField={() => clearField('description')}
                label={msg({ id: 'field.description.label' })}
                control={control}
              />
            </MDBox>
          )}
        />
        <Controller
          name="provider"
          control={control}
          rules={{ required: msg({ id: 'field.provider.required' }) }}
          render={({ field }) => (
            <FormControl variant="filled" fullWidth required size="small" error={!!errors.provider}>
              <InputLabel id="provider-select-label">
                {msg({ id: 'page.aiModels.form.provider' })}
              </InputLabel>
              <Select
                {...field}
                labelId="provider-select-label"
                value={field.value?.id || ''}
                onChange={(e) => {
                  const providerId = e.target.value;
                  const selectedProvider = providers.find((p) => p.id === providerId);
                  field.onChange(selectedProvider);
                }}
              >
                {providers.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.provider && <FormHelperText>{errors.provider.message}</FormHelperText>}
            </FormControl>
          )}
        />
        <Controller
          name="maxTokens"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              type="number"
              label={msg({ id: 'page.aiModels.form.maxTokens' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.maxTokens}
              helperText={errors.maxTokens ? errors.maxTokens.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => setValue('maxTokens', '')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="temperature"
          control={control}
          render={({ field }) => (
            <MDBox
              sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
              <Typography gutterBottom>
                {msg({ id: 'page.aiModels.form.temperature' })}: {field.value}
              </Typography>
              <Slider
                value={field.value}
                onChange={(_, newValue) => field.onChange(newValue)}
                step={0.1}
                marks
                min={0}
                max={1}
                sx={{ width: '90%' }}
                valueLabelDisplay="auto"
              />
            </MDBox>
          )}
        />
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

AiModelDetails.propTypes = {
  model: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    label: PropTypes.string,
    description: PropTypes.string,
    temperature: PropTypes.number,
    maxTokens: PropTypes.number,
    provider: PropTypes.object
  }),
  onSubmit: PropTypes.func,
  onFieldChange: PropTypes.func
};

export default AiModelDetails;
